import React from 'react';
import { Link } from 'react-router-dom';
import { Download, Facebook, Twitter, Linkedin } from 'lucide-react';
import LandingHeader from '@/components/landing/LandingHeader';
import { usePageMeta } from '@/hooks/usePageMeta';
import { Button } from '@/components/ui/button';
import lanerunnerLogoWhite from '@/assets/lanerunner-logo-white.png';
import onePagerPreview from '@/assets/investor-one-pager-preview.jpg';

const InvestorsPage: React.FC = () => {
  usePageMeta({ title: 'Investors', description: 'LaneRunner investor overview — one-pager and company video.' });

  return (
    <div className="min-h-screen bg-white">
      <LandingHeader />

      {/* Hero */}
      <section className="pt-28 pb-12 sm:pt-32 sm:pb-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Investor Overview
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
            Everything you need to know about LaneRunner — our vision, traction, and opportunity.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20 sm:pb-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-16">
          {/* Video Placeholder */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4 text-center">Company Video</h2>
            <video
              src="/docs/Lanerunner_investor_promo.mp4"
              controls
              className="w-full aspect-video rounded-xl shadow-lg bg-black"
            />
          </div>

          {/* One-Pager Preview + Download */}
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">One-Pager</h2>
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden inline-block">
              <img
                src={onePagerPreview}
                alt="LaneRunner One-Pager Preview"
                className="w-full max-w-2xl"
              />
            </div>
            <div className="mt-6 space-y-3 flex flex-col items-center">
              <Button asChild variant="action" size="lg">
                <a href="/docs/Lane_Runner_One-Page.pdf" download>
                  <Download className="w-5 h-5 mr-2" />
                  Download One-Pager (PDF)
                </a>
              </Button>
              <p className="text-muted-foreground text-sm">
                Contact: <a href="mailto:tim@lanerunner.com" className="text-primary hover:underline">tim@lanerunner.com</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="col-span-2 md:col-span-1">
              <img src={lanerunnerLogoWhite} alt="LaneRunner" className="h-14 sm:h-16 mb-4" />
              <p className="text-gray-400 text-sm">AI-powered freight matching platform.</p>
            </div>
            <div>
              <h4 className="text-white mb-4">Platform</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/shippers" className="hover:text-[#1ab7aa] transition-colors">Shippers</Link></li>
                <li><Link to="/brokers" className="hover:text-[#1ab7aa] transition-colors">Brokers</Link></li>
                <li><Link to="/carriers" className="hover:text-[#1ab7aa] transition-colors">Carriers</Link></li>
                <li><Link to="/pricing" className="hover:text-[#1ab7aa] transition-colors">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/about" className="hover:text-[#1ab7aa] transition-colors">About</Link></li>
                <li><Link to="/investors" className="hover:text-[#1ab7aa] transition-colors">Investors</Link></li>
                <li><Link to="/affiliates" className="hover:text-[#1ab7aa] transition-colors">Affiliate Program</Link></li>
                <li><Link to="/privacy" className="hover:text-[#1ab7aa] transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-[#1ab7aa] transition-colors">Terms of Service</Link></li>
                <li><Link to="/cancellation-policy" className="hover:text-[#1ab7aa] transition-colors">Cancellation Policy</Link></li>
                <li><Link to="/cookies" className="hover:text-[#1ab7aa] transition-colors">Cookie Policy</Link></li>
                <li><button type="button" onClick={() => window.dispatchEvent(new Event('lr-open-cookie-settings'))} className="hover:text-[#1ab7aa] transition-colors text-left">Cookie Preferences</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white mb-4">Connect</h4>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-[#1ab7aa] transition-colors"><Facebook className="w-5 h-5" /></a>
                <a href="#" className="text-gray-400 hover:text-[#1ab7aa] transition-colors"><Twitter className="w-5 h-5" /></a>
                <a href="#" className="text-gray-400 hover:text-[#1ab7aa] transition-colors"><Linkedin className="w-5 h-5" /></a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm">
            © {new Date().getFullYear()} LaneRunner. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default InvestorsPage;
